import { createContext, useContext } from 'react';
import type { Lang, Translations, TranslationValue } from '@inno-ck/shared';
import { getTranslations } from '@inno-ck/shared';

/**
 * React context for language setting
 * 
 * This is the Imperative Shell - manages runtime state.
 */
export const LangContext = createContext<Lang>('fr');

/**
 * Hook to access translations
 * 
 * @returns The translations object for the current language
 * 
 * @example
 * const tr = useTranslation();
 * console.log(tr.home); // "Accueil" (fr) or "Home" (en)
 */
export const useTranslation = (): Translations => {
  const lang = useContext(LangContext);
  return getTranslations(lang);
};

/**
 * Alias for backward compatibility with existing code
 */
export const useTr = useTranslation;

/**
 * Coerce a TranslationValue to a plain string.
 * 
 * If the value is a function (interpolation template), it is called with no args.
 * If the value is undefined (key miss), returns the empty string.
 * Otherwise returns the string as-is.
 *
 * Usage:
 *   const tr = useTr();
 *   <span>{t(tr.home)}</span>              // string value
 *   <span>{t(tr.deleteConfirm, name)}</span> // function value with args
 */
export function t(v: TranslationValue | undefined, ...args: unknown[]): string {
  if (v === undefined) return '';
  if (typeof v === 'function') return v(...args);
  return v;
}

export { useAutosave, useClickOutside, useHistory, useFileUpload } from './canvas';
export type { SaveStatus, History } from './canvas';
